<script setup lang="ts">
import type { PropType } from 'vue';
import GaiaDeleteButton from '../base/GaiaDeleteButton.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  /** Second line, e.g. "Page 2 · Lena Brandt" on a placed field. */
  meta: {
    type: String,
    default: undefined,
  },
  /** `warning` when the second line says something is still missing. */
  metaTone: {
    type: String as PropType<'subtle' | 'warning'>,
    default: 'subtle',
  },
  /** Colour dot instead of the #icon slot — the signer swatches. */
  dot: {
    type: String,
    default: undefined,
  },
  /** Armed: the field type that the next click on the page will place. */
  active: {
    type: Boolean,
    default: false,
  },
  /** Show the trailing remove control (the placed-fields list). */
  removable: {
    type: Boolean,
    default: false,
  },
  /**
   * Also draggable onto the page, not just click-to-arm — the overlay accepts
   * both. The view listens for `dragstart` through attrs fallthrough and puts
   * the field type on the dataTransfer.
   */
  draggable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ select: []; remove: [] }>();

const border = gaiaNeutral.border;
const borderHover = gaiaNeutral.borderHover;
const surface = gaiaNeutral.surface;
const activeBg = gaiaNeutral.surfaceTint;
const ink = gaiaNeutral.ink;
const faint = gaiaNeutral.textFaint;
</script>

<template>
  <!-- A selectable row in the field-placement rails (wizard step 4): the field
       palette, the signer swatches and the placed-field list are the same row
       with a different leading mark. Bespoke because the armed state is a
       border + tint that no naive list row carries. -->
  <n-flex
    align="center"
    :size="6"
    :wrap="false"
    class="gaia-palette-item"
    :class="{ active: props.active, draggable: props.draggable }"
    :draggable="props.draggable || undefined"
  >
    <button type="button" class="main" @click="emit('select')">
      <span v-if="props.dot" class="swatch" :style="{ background: props.dot }" />
      <n-flex
        v-else-if="$slots.icon"
        inline
        :size="0"
        :wrap="false"
        :style="{ flex: 'none', color: faint }"
      >
        <slot name="icon" />
      </n-flex>
      <n-flex vertical :size="0" :style="{ minWidth: 0 }">
        <gaia-text truncate size="control" :weight="props.active ? 'semibold' : 'regular'">
          {{ props.label }}
        </gaia-text>
        <gaia-text v-if="props.meta" truncate size="micro" :tone="props.metaTone">
          {{ props.meta }}
        </gaia-text>
      </n-flex>
    </button>
    <gaia-delete-button
      v-if="props.removable"
      intent="remove"
      size="xs"
      :subject="props.label"
      @click="emit('remove')"
    />
  </n-flex>
</template>

<style scoped>
.gaia-palette-item {
  padding: 0 9px 0 10px;
  min-height: 32px;
  border: 1px solid v-bind(border);
  border-radius: 8px;
  background: v-bind(surface);
}

.gaia-palette-item:hover {
  border-color: v-bind(borderHover);
}

.gaia-palette-item.active {
  border-color: v-bind(ink);
  background: v-bind(activeBg);
}

/* Draggable rows say so under the pointer; the click-to-arm path still works,
   so the row keeps its button semantics underneath. */
.gaia-palette-item.draggable,
.gaia-palette-item.draggable .main {
  cursor: grab;
}

.gaia-palette-item.draggable:active {
  cursor: grabbing;
}

.main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 0;
  border: none;
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.swatch {
  width: 7px;
  height: 7px;
  flex: none;
  border-radius: 50%;
}
</style>
